<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'

type CalcRowVariant = 'item' | 'group' | 'total' | 'gap'

const props = withDefaults(
  defineProps<{
    label: string
    variant?: CalcRowVariant
    sign?: string
    hint?: string
    required?: boolean
    indent?: boolean
    gutter?: boolean
    dense?: boolean
    conversion?: string | null
    value?: string | null
  }>(),
  {
    variant: 'item',
    sign: '',
    required: false,
    indent: false,
    gutter: true,
    dense: false,
    conversion: null,
    value: null,
  },
)

const { t } = useI18n({ useScope: 'local' })

const slots = useSlots()

const rowClasses: Record<CalcRowVariant, string> = {
  item: 'py-1 sm:min-h-[52px]',
  group: 'pt-2 pb-1',
  total: 'border-line mt-3 border-t pt-3',
  gap: 'py-1 sm:min-h-[52px]',
}

const labelClasses: Record<CalcRowVariant, string> = {
  item: 'text-ink-2 text-base leading-tight',
  group: 'section-label section-label--sm',
  total: 'text-ink text-lg font-black tracking-tight',
  gap: 'text-ink text-base leading-tight font-semibold',
}

const valueClasses: Record<CalcRowVariant, string> = {
  item: 'text-ink text-base font-bold',
  group: 'text-ink-2 text-sm font-bold',
  total: 'text-brand-blue-1 text-lg font-black tracking-tight',
  gap: 'text-warning text-base font-bold',
}

/**
 * Every row keeps label and value side by side while both fit, and stacks them in reading order once
 * the card gets too narrow. A row with an input field needs the turn earlier, because a field stays
 * usable only at a certain width, while a value column takes exactly the width its number needs.
 */
const withField = computed(() => Boolean(slots.default))

const valueLayouts = {
  gutter: {
    grid: 'grid-cols-[1.25rem_minmax(0,1fr)] @min-[340px]:grid-cols-[1.25rem_minmax(0,1fr)_max-content]',
    value: 'col-start-2 row-start-2 @min-[340px]:col-start-3 @min-[340px]:row-start-1',
    meta: 'col-start-2 row-start-3 @min-[340px]:col-start-3 @min-[340px]:row-start-2',
  },
  plain: {
    grid: 'grid-cols-1 @min-[300px]:grid-cols-[minmax(0,1fr)_max-content]',
    value: 'col-start-1 row-start-2 @min-[300px]:col-start-2 @min-[300px]:row-start-1',
    meta: 'col-start-1 row-start-3 @min-[300px]:col-start-2 @min-[300px]:row-start-2',
  },
}

const fieldLayouts = {
  gutter: {
    grid: 'grid-cols-[1.25rem_minmax(0,1fr)] @min-[480px]:grid-cols-[1.25rem_minmax(0,1fr)_minmax(7rem,var(--calc-value,220px))]',
    value: 'col-start-2 row-start-2 @min-[480px]:col-start-3 @min-[480px]:row-start-1',
    meta: 'col-start-2 row-start-3 @min-[480px]:col-start-3 @min-[480px]:row-start-2',
  },
  plain: {
    grid: 'grid-cols-1 @min-[380px]:grid-cols-[minmax(0,1fr)_minmax(7rem,var(--calc-value,220px))]',
    value: 'col-start-1 row-start-2 @min-[380px]:col-start-2 @min-[380px]:row-start-1',
    meta: 'col-start-1 row-start-3 @min-[380px]:col-start-2 @min-[380px]:row-start-2',
  },
}

/** A side card is narrower than any field row needs, so `dense` trades label room for the side by side turn. */
const denseFieldLayouts = {
  gutter: {
    grid: 'grid-cols-[1.25rem_minmax(0,1fr)] @min-[360px]:grid-cols-[1.25rem_minmax(0,1fr)_minmax(7rem,var(--calc-value,220px))]',
    value: 'col-start-2 row-start-2 @min-[360px]:col-start-3 @min-[360px]:row-start-1',
    meta: 'col-start-2 row-start-3 @min-[360px]:col-start-3 @min-[360px]:row-start-2',
  },
  plain: {
    grid: 'grid-cols-1 @min-[260px]:grid-cols-[minmax(0,1fr)_minmax(7rem,var(--calc-value,220px))]',
    value: 'col-start-1 row-start-2 @min-[260px]:col-start-2 @min-[260px]:row-start-1',
    meta: 'col-start-1 row-start-3 @min-[260px]:col-start-2 @min-[260px]:row-start-2',
  },
}

const layout = computed(() => {
  const column = props.gutter ? 'gutter' : 'plain'

  if (!withField.value) {
    return valueLayouts[column]
  }

  return props.dense ? denseFieldLayouts[column] : fieldLayouts[column]
})

const labelClass = computed(() => (props.gutter ? 'col-start-2 row-start-1' : 'col-start-1 row-start-1'))
</script>

<template>
  <div class="grid items-center gap-x-3 gap-y-0.5" :class="[layout.grid, rowClasses[variant]]">
    <span v-if="gutter" class="text-ink-3 col-start-1 row-start-1 text-center text-lg font-light" aria-hidden="true">
      {{ sign }}
    </span>

    <div class="min-w-0 break-words" :class="[labelClass, labelClasses[variant], indent ? 'pl-3' : '']">
      <span>{{ label }}</span>
      <span v-if="required" class="text-danger" aria-hidden="true">*</span>
      <span v-if="required" class="sr-only">{{ t('required') }}</span>

      <span v-if="hint || $slots.sub" class="text-ink-3 flex flex-wrap items-center gap-x-2 text-xs font-normal">
        <span v-if="hint">{{ hint }}</span>
        <slot name="sub" />
      </span>
    </div>

    <div :class="layout.value">
      <slot>
        <span class="block pr-3 text-right tabular-nums" :class="valueClasses[variant]">{{ value }}</span>
      </slot>
    </div>

    <div
      v-if="conversion || $slots.meta"
      class="text-ink-3 flex items-center justify-end gap-2 pr-3 text-right text-xs whitespace-nowrap tabular-nums"
      :class="layout.meta">
      <slot name="meta">{{ conversion }}</slot>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "required": "Pflichtfeld"
  },
  "en": {
    "required": "Required"
  }
}
</i18n>
